import { useState, useCallback, useEffect, useRef } from 'react'
import { BuilderContext } from './BuilderContextInstance'

export const DEVICE_WIDTHS = { mobile: '375px', tablet: '768px', desktop: '100%' }

// New blocs have no `nid` yet — assigned by the backend on first save.
const makeBloc = (blockDef) => ({
  id: crypto.randomUUID(),
  nid: null,
  type: blockDef.type,
  data: { ...(blockDef.defaultData || {}) },
  hiddenOn: [],
  // Grid position (2-D layout mode). Empty on add → the grid assigns a default
  // slot; drag/resize then fills real {x,y,w,h}. Ignored in linear mode.
  layout: {},
})

/**
 * `editScope` — CE QUE cette session d'édition modifie.
 *
 *  - `'page'`   (défaut) : la page et ses blocs, comme depuis toujours.
 *  - `'object'` : une INSTANCE D'OBJET regardée à travers une page-modèle. Le
 *    modèle n'est alors qu'une lunette : sa structure est inerte (aucune barre
 *    de bloc, aucun liseré d'ajout, aucun rail de ligne, aucun bloc partagé
 *    modifiable) et seules ses liaisons `~~._context_.X` s'éditent — vers
 *    l'objet, jamais vers la page. `objectEdit` porte alors le contrat de
 *    lecture/écriture de cet objet ({fields, mapping, resolve, getValue,
 *    setValue}), fourni par `BuilderObjectEditor`.
 */
export const BuilderProvider = ({ children, slug, initialEditMode = false, initialLayoutMode = 'linear', initialHost = null, readOnly = false, editScope = 'page', objectEdit = null }) => {
  // `published` = statut de publication de la page (node builder_page). Une page
  // encore inexistante démarre publiée, comme le défaut Drupal à la création.
  const [page, setPage] = useState({ slug: slug || '', title: '', published: true, blocs: [] })
  // Always-current mirror of `page` for synchronous reads inside callbacks
  // (e.g. `setGridLayout` computes `changed` before dispatching — never read a
  // React state updater's result outside it). Updated during render → current.
  const pageRef = useRef(page)
  pageRef.current = page
  const [isEditMode, setEditMode] = useState(initialEditMode)
  // Layout strategy for the canvas: 'linear' (vertical block stack, the page
  // default) or 'grid' (2-D react-grid-layout — dashboards, lot L1). The bloc's
  // position `{x,y,w,h}` is per-bloc structural metadata (like `hiddenOn`),
  // written by `setGridLayout` on drag/resize END.
  const [layoutMode, setLayoutMode] = useState(initialLayoutMode)
  // Host descriptor: `null` = the default `builder_page`/slug host;
  // `{ kind: 'dashboard', id }` routes load/save through `hostAdapters` against a
  // single `field_layout` blob. The resolved save metadata (its decoupled
  // submission URL) is harvested at load time into `page.hostMeta`.
  const [host] = useState(initialHost)
  const [registry, setRegistry] = useState([])
  const [activeEditor, setActiveEditor] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [previewDevice, setPreviewDevice] = useState('desktop')

  // Un bloc « délié » garde, LE TEMPS DE LA SESSION D'ÉDITION, la mémoire de
  // l'unité qu'il suivait (`{blocId: sharedRef}`) : c'est ce qui permet de le
  // RELIER d'un clic, au même endroit, sans repasser par la bibliothèque.
  // Volontairement HORS de `page` — ce n'est pas du contenu, rien n'en est
  // sauvegardé : après un rechargement la séparation est définitive, et c'est
  // ce que dit le bouton.
  const [detachedShares, setDetachedShares] = useState({})

  // Content persistence cadence (Lot S). `autoSave` is a global workflow toggle:
  //   - false (default) = MANUAL — per-unit DataSyncs hold edits in their store
  //     and only POST when flushed (the global Save button bumps `flushVersion`);
  //   - true            = AUTO — DataSyncs persist on idle (their native mode).
  // `contentDirty` tracks unsaved per-unit content (manual mode only); structure
  // edits keep using `isDirty`. The Save button unifies both visually.
  const [autoSave, setAutoSave] = useState(false)
  const [flushVersion, setFlushVersion] = useState(0)
  const [contentDirty, setContentDirty] = useState(false)

  // Bump the flush counter — every per-unit DataSync watching it persists its
  // pending content (owner-only for shared-syncable groups, so one coalesced POST).
  const flushAll = useCallback(() => setFlushVersion(v => v + 1), [])
  const markContentDirty = useCallback(() => setContentDirty(true), [])
  const clearContentDirty = useCallback(() => setContentDirty(false), [])

  // Warn before leaving with unsaved work — structure (isDirty) OR, in manual
  // mode, per-unit content held in memory (contentDirty) that a reload would drop.
  useEffect(() => {
    if (!isDirty && !contentDirty) return
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty, contentDirty])

  /**
   * `value` accepte une FONCTION de l'ancienne valeur, comme `setState`.
   *
   * Deux blocs demandés à l'assistant en parallèle aboutissent l'un après
   * l'autre : la seconde pose doit se ranger à côté de la première, pas
   * par-dessus. Passer une valeur calculée à l'avance (depuis un miroir de
   * rendu) la lui ferait écraser si les deux réponses tombaient de près.
   */
  const updateBlock = useCallback((id, fieldKey, value) => {
    setPage(prev => ({
      ...prev,
      blocs: prev.blocs.map(b =>
        b.id === id
          ? { ...b, data: { ...b.data, [fieldKey]: typeof value === 'function' ? value(b.data?.[fieldKey]) : value } }
          : b
      ),
    }))
    setIsDirty(true)
  }, [])

  const addBlock = useCallback((blockDef, position) => {
    const newBloc = makeBloc(blockDef)
    setPage(prev => {
      const blocs = [...prev.blocs]
      blocs.splice(position, 0, newBloc)
      return { ...prev, blocs }
    })
    setIsDirty(true)
  }, [])

  /**
   * Append an instance of `blockDef` UNLESS the canvas already holds one of
   * that type. Used when the chat assistant creates a block type: the editor
   * asked for it, so it has to be visible without hunting for it in the
   * catalogue. Returns true when a bloc was actually added.
   *
   * Reads/advances `pageRef` rather than `page` so several types touched by
   * the same turn append in order (the state mirror only refreshes on render).
   */
  const ensureBlockAtEnd = useCallback((blockDef) => {
    if (!blockDef?.type) return false
    if (pageRef.current.blocs.some(b => b.type === blockDef.type)) return false
    const newBloc = makeBloc(blockDef)
    pageRef.current = { ...pageRef.current, blocs: [...pageRef.current.blocs, newBloc] }
    setPage(prev => ({ ...prev, blocs: [...prev.blocs, newBloc] }))
    setIsDirty(true)
    return true
  }, [])

  const toggleBlockVisibility = useCallback((blockId, device) => {
    setPage(prev => ({
      ...prev,
      blocs: prev.blocs.map(b => {
        if (b.id !== blockId) return b
        const hiddenOn = [...(b.hiddenOn || [])]
        const idx = hiddenOn.indexOf(device)
        if (idx >= 0) hiddenOn.splice(idx, 1)
        else hiddenOn.push(device)
        return { ...b, hiddenOn }
      }),
    }))
    // `hiddenOn` is unit CONTENT (ADR-001 lot L1), not page structure: the
    // page-state change re-seeds the unit's store, so its per-unit DataSync
    // persists the new visibility (on idle in auto mode, on flush in manual).
    // Marking it content-dirty (not isDirty) keeps the proxy from also writing
    // the node — a double write would race the DataSync flush (EntityChanged).
    // A brand-new block (no submission_url yet) still persists via the proxy on
    // first save, where addBlock already set isDirty.
    markContentDirty()
  }, [markContentDirty])

  /**
   * Remplace un bloc par un autre AU MÊME EMPLACEMENT — identifiant neuf, pas de
   * `nid`. À la sauvegarde, une unité neuve est créée et l'ancienne devient
   * orpheline (donc supprimée, sauf si un autre contenu la référence encore).
   *
   * C'est ce qui « délie » un bloc partagé : le porteur est remplacé par un
   * bloc ordinaire portant une COPIE du contenu, qui ne suit donc plus les
   * éditions faites ailleurs. Sans `nid`, aucun risque de réécrire l'unité
   * commune — le partage se défait par création, jamais par mutation.
   *
   * `detachedFrom` (le `sharedRef` que portait le porteur) est mémorisé contre
   * le bloc NEUF : le remplacement tire un identifiant, l'ancien disparaît avec
   * le bloc. C'est cette mémoire qui allume le bouton « Relier ».
   */
  const replaceBlock = useCallback((id, blockDef, { data, hiddenOn, detachedFrom } = {}) => {
    // Construit HORS de l'updater : celui-ci peut être rejoué (StrictMode), et
    // deux exécutions tireraient deux identifiants — dont un seul serait celui
    // enregistré ci-dessous.
    const replacement = {
      ...makeBloc(blockDef),
      data: data ?? { ...(blockDef.defaultData || {}) },
      hiddenOn: hiddenOn ?? [],
    }
    setPage(prev => ({
      ...prev,
      blocs: prev.blocs.map(b => (b.id === id
        ? {
          ...replacement,
          // La position en grille appartient à la PLACE, pas au bloc remplacé.
          layout: b.layout ?? {},
        }
        : b)),
    }))
    setDetachedShares((prev) => {
      if (!detachedFrom && !(id in prev)) return prev
      const next = { ...prev }
      delete next[id]
      if (detachedFrom) next[replacement.id] = detachedFrom
      return next
    })
    setIsDirty(true)
  }, [])

  const removeBlock = useCallback((id) => {
    setPage(prev => ({
      ...prev,
      blocs: prev.blocs.filter(b => b.id !== id),
    }))
    setIsDirty(true)
  }, [])

  const moveBlock = useCallback((id, direction) => {
    setPage(prev => {
      const blocs = [...prev.blocs]
      const idx = blocs.findIndex(b => b.id === id)
      if (idx === -1) return prev
      if (direction === 'up' && idx === 0) return prev
      if (direction === 'down' && idx === blocs.length - 1) return prev
      const target = direction === 'up' ? idx - 1 : idx + 1
      ;[blocs[idx], blocs[target]] = [blocs[target], blocs[idx]]
      return { ...prev, blocs }
    })
    setIsDirty(true)
  }, [])

  const reorderBlocks = useCallback((orderedIds) => {
    setPage(prev => {
      const blocMap = Object.fromEntries(prev.blocs.map(b => [b.id, b]))
      const blocs = orderedIds.map(id => blocMap[id]).filter(Boolean)
      return { ...prev, blocs }
    })
    setIsDirty(true)
  }, [])

  // Grid layout (2-D mode): react-grid-layout hands back the full layout array
  // (`[{i, x, y, w, h}]`, `i` = bloc.id) on drag/resize END. Write the new
  // position into each bloc's `layout`, only when it actually changed (avoids a
  // render loop / spurious isDirty). Structure edit → `isDirty`.
  const setGridLayout = useCallback((rglLayout) => {
    const byId = Object.fromEntries((rglLayout || []).map(l => [l.i, l]))
    // Compute `changed` + the next blocs SYNCHRONOUSLY against the current state
    // (via pageRef) — don't read a setState updater's side effect from outside
    // it (React 19 may defer/batch the updater, so `isDirty` would be skipped).
    const blocs = pageRef.current.blocs
    let changed = false
    const nextBlocs = blocs.map(b => {
      const l = byId[b.id]
      if (!l) return b
      const cur = b.layout || {}
      if (cur.x === l.x && cur.y === l.y && cur.w === l.w && cur.h === l.h) return b
      changed = true
      return { ...b, layout: { x: l.x, y: l.y, w: l.w, h: l.h } }
    })
    if (!changed) return
    setPage(prev => ({ ...prev, blocs: nextBlocs }))
    setIsDirty(true)
  }, [])

  // Statut de publication : donnée de PAGE (comme le titre ou le slug), donc
  // écrite par le proxy orchestré au Save → `isDirty`, pas `contentDirty` (qui
  // ne concerne que le contenu des blocs porté par DataSync).
  const setPagePublished = useCallback((published) => {
    setPage(prev => (prev.published === published ? prev : { ...prev, published }))
    setIsDirty(true)
  }, [])

  const activateEditor = useCallback((blocId, fieldKey) => {
    setActiveEditor({ blocId, fieldKey })
  }, [])

  const deactivateEditor = useCallback(() => {
    setActiveEditor(null)
  }, [])

  return (
    <BuilderContext.Provider value={{
      readOnly,
      editScope,
      objectEdit,
      page,
      setPage,
      isEditMode,
      setEditMode,
      registry,
      setRegistry,
      activeEditor,
      activateEditor,
      deactivateEditor,
      isSaving,
      setIsSaving,
      isDirty,
      setIsDirty,
      setPagePublished,
      updateBlock,
      addBlock,
      ensureBlockAtEnd,
      removeBlock,
      replaceBlock,
      detachedShares,
      moveBlock,
      reorderBlocks,
      layoutMode,
      setLayoutMode,
      setGridLayout,
      host,
      previewDevice,
      setPreviewDevice,
      toggleBlockVisibility,
      autoSave,
      setAutoSave,
      flushVersion,
      flushAll,
      contentDirty,
      markContentDirty,
      clearContentDirty,
    }}>
      {children}
    </BuilderContext.Provider>
  )
}
